import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, ShieldCheck, Boxes, Thermometer, Truck, MapPin, Radio, Building2, Layers3, Sparkles } from "lucide-react";
import api from "@/api";
import { ProviderLogo, VerifiedBadge, FeaturedBadge } from "@/components/ProviderCard";
import Seo from "@/components/Seo";

const USE_CASES = [
  { label: "Cold chain monitoring", q: "category=Cold chain monitoring", icon: Thermometer },
  { label: "Shipment tracking", q: "category=Shipment tracking", icon: Truck },
  { label: "Asset tracking", q: "category=Asset tracking", icon: Boxes },
  { label: "Real-time location", q: "category=Real-time location tracking", icon: MapPin },
  { label: "BLE / Indoor positioning", q: "category=BLE location tracking", icon: Radio },
  { label: "Returnable packaging", q: "category=Returnable packaging tracking", icon: Layers3 },
];

const INDUSTRIES = [
  "Life sciences and pharma", "Cold chain", "Logistics", "Manufacturing",
  "Food and beverage", "Automotive", "Retail", "Aerospace",
];

const TECH = [
  "GPS/GNSS", "Cellular location", "Bluetooth Low Energy", "LoRaWAN",
  "Satellite", "NB-IoT", "5G", "UWB",
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [q, setQ] = useState("");
  const [stats, setStats] = useState({ total: 0, verified: 0, countries: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/providers/featured?limit=6").then(({ data }) => setFeatured(data.items || []));
    api.get("/providers?page=1&page_size=1").then(({ data }) => {
      setStats((s) => ({ ...s, total: data.total }));
    });
  }, []);

  const goSearch = (e) => {
    e?.preventDefault();
    const qs = q ? `?q=${encodeURIComponent(q)}` : "";
    navigate(`/solutions${qs}`);
  };

  return (
    <div>
      <Seo
        title="Find & Compare Tracking Solutions Globally"
        description="Specialist B2B directory of tracking device manufacturers, GPS trackers, BLE tags, smart labels, cold chain monitoring systems and asset tracking solutions. Compare verified providers and request quotes."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Strategic Tracking",
          "url": typeof window !== "undefined" ? window.location.origin : "",
          "potentialAction": {
            "@type": "SearchAction",
            "target": (typeof window !== "undefined" ? window.location.origin : "") + "/solutions?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 hero-grid-bg pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full" style={{background: "radial-gradient(circle, rgba(0,47,167,0.06), transparent 70%)"}} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-xs font-semibold tracking-wider uppercase text-slate-700 mb-6" data-testid="hero-eyebrow">
              <Sparkles size={12}/> Specialist B2B tracking solution finder
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold text-slate-900 tracking-tighter leading-[1.02]">
              Find and compare<br/>
              <span className="brand-text">tracking solutions</span> globally.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed mx-auto">
              Search tracking device manufacturers, visibility platforms, smart label providers,
              GPS trackers, cold chain monitoring systems and asset tracking solutions by use case,
              technology, sensor capability, industry and region.
            </p>

            <form onSubmit={goSearch} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto" data-testid="hero-search-form">
              <div className="flex-1 relative">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={q}
                  onChange={(e)=>setQ(e.target.value)}
                  placeholder="Search tracking devices, sensors, providers, regions…"
                  className="w-full h-14 pl-12 pr-4 rounded-lg border border-slate-300 focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none text-slate-900 placeholder:text-slate-400 text-left"
                  data-testid="hero-search-input"
                />
              </div>
              <button type="submit" data-testid="hero-search-submit" className="btn-primary h-14 px-7 text-base">
                Search Solutions <ArrowRight size={18}/>
              </button>
            </form>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-sm">
              <Link to="/request-quotes" data-testid="hero-cta-quotes" className="text-[#002FA7] font-semibold hover:underline inline-flex items-center gap-1">
                Request provider recommendations <ArrowRight size={14}/>
              </Link>
              <span className="text-slate-300">|</span>
              <Link to="/pricing" data-testid="hero-cta-list" className="text-slate-700 font-semibold hover:text-slate-900 inline-flex items-center gap-1">
                List your company <ArrowRight size={14}/>
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 max-w-xl gap-6 border-t border-slate-200 pt-6 mx-auto">
              <div>
                <div className="font-display font-extrabold text-2xl text-slate-900" data-testid="stat-providers">{stats.total || "—"}</div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">Providers indexed</div>
              </div>
              <div>
                <div className="font-display font-extrabold text-2xl text-slate-900">22</div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">Solution categories</div>
              </div>
              <div>
                <div className="font-display font-extrabold text-2xl text-slate-900">Global</div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">Coverage</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="bg-slate-50 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Selected providers</div>
                <h2 className="font-display font-bold text-3xl text-slate-900 mt-1">Featured tracking solution providers</h2>
              </div>
              <Link to="/solutions?featured_only=true" className="btn-secondary" data-testid="link-featured">View all featured <ArrowRight size={14}/></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {featured.map((p) => (
                <Link key={p.id} to={`/solutions/${p.slug}`} className="card-elevated p-5 block" data-testid={`featured-${p.slug}`}>
                  <div className="flex items-start gap-3">
                    <ProviderLogo provider={p} size={48}/>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display font-bold text-slate-900 truncate">{p.name}</div>
                        {p.verified && <VerifiedBadge/>}
                        {p.featured && <FeaturedBadge/>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{[p.hq_city, p.hq_country].filter(Boolean).join(", ")}</div>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-slate-600 line-clamp-3">{p.tagline || p.summary}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(p.solution_categories || []).slice(0, 3).map((c) => <span key={c} className="tag">{c}</span>)}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* USE CASES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Browse by</div>
            <h2 className="font-display font-bold text-3xl text-slate-900 mt-1">Popular solution categories</h2>
          </div>
          <Link to="/solutions" className="btn-secondary" data-testid="link-all-solutions">All solutions <ArrowRight size={14}/></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {USE_CASES.map((u) => {
            const Icon = u.icon;
            return (
              <Link key={u.label} to={`/solutions?${u.q}`} data-testid={`usecase-${u.label.replace(/\s+/g, '-').toLowerCase()}`}
                className="card-elevated p-5 group">
                <div className="w-10 h-10 rounded-md bg-slate-100 grid place-items-center group-hover:bg-[#002FA7] group-hover:text-white transition-colors">
                  <Icon size={18} className="text-slate-700 group-hover:text-white"/>
                </div>
                <div className="mt-4 font-display font-semibold text-slate-900 leading-tight">{u.label}</div>
                <div className="mt-1 text-xs text-slate-500 inline-flex items-center gap-1 group-hover:text-[#002FA7]">View providers <ArrowRight size={12}/></div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* INDUSTRY + TECH GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-10">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">By industry</div>
          <h2 className="font-display font-bold text-3xl text-slate-900 mt-1 mb-6">Source by industry vertical</h2>
          <div className="flex flex-wrap gap-2">
            {INDUSTRIES.map((i) => (
              <Link key={i} to={`/solutions?industry=${encodeURIComponent(i)}`} data-testid={`industry-${i.replace(/\s+/g, '-').toLowerCase()}`}
                className="px-4 py-2 rounded-md border border-slate-200 bg-white hover:border-slate-900 hover:text-slate-900 text-sm font-medium text-slate-700 transition-colors">
                {i}
              </Link>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">By technology</div>
          <h2 className="font-display font-bold text-3xl text-slate-900 mt-1 mb-6">Filter by connectivity</h2>
          <div className="flex flex-wrap gap-2">
            {TECH.map((t) => (
              <Link key={t} to={`/solutions?connectivity=${encodeURIComponent(t)}`} data-testid={`tech-${t.replace(/\s+/g, '-').toLowerCase()}`}
                className="px-4 py-2 rounded-md border border-slate-200 bg-white hover:border-[#002FA7] hover:text-[#002FA7] text-sm font-medium text-slate-700 transition-colors font-mono">
                {t}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-3 gap-10">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">Why Strategic Tracking</div>
            <h2 className="font-display font-bold text-3xl text-white mt-1">The procurement tool, not a passive directory.</h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Shortlist verified tracking technology providers by use case, technology,
              sensor capability, region and commercial model — then request quotes in one place.
            </p>
            <Link to="/solutions" className="mt-6 inline-flex items-center gap-2 text-white font-semibold border-b border-white/30 hover:border-white pb-0.5" data-testid="why-cta-search">
              Start your search <ArrowRight size={16}/>
            </Link>
          </div>
          <div className="md:col-span-2 grid sm:grid-cols-2 gap-4">
            {[
              { t: "Specialist focus", d: "Built only for tracking, condition monitoring and supply chain visibility — no generic directory bloat.", icon: ShieldCheck },
              { t: "Technical filtering", d: "Filter by sensor type, connectivity, battery life, compliance, commercial model and integration capability.", icon: Layers3 },
              { t: "Verified providers", d: "Claimed and verified listings carry trust badges and direct quote routing.", icon: Building2 },
              { t: "One-stop quote requests", d: "Compare and request quotes from multiple providers in a single workflow.", icon: Truck },
            ].map((f) => (
              <div key={f.t} className="border border-white/10 rounded-lg p-5 bg-white/[0.03]">
                <f.icon size={18} className="text-orange-400"/>
                <div className="mt-3 font-display font-semibold text-white">{f.t}</div>
                <div className="mt-1 text-sm text-slate-400 leading-relaxed">{f.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA bands */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-6">
        <div className="card-elevated p-8 bg-white">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">For buyers</div>
          <h3 className="font-display font-bold text-2xl text-slate-900 mt-1">Need help shortlisting providers?</h3>
          <p className="mt-2 text-slate-600">Tell us about your project and we'll surface a curated shortlist of relevant tracking providers.</p>
          <Link to="/request-quotes" className="btn-primary mt-6" data-testid="cta-buyer-quote">Request quotes <ArrowRight size={16}/></Link>
        </div>
        <div className="card-elevated p-8" style={{background: "#002FA7", borderColor: "#002FA7"}}>
          <div className="text-xs font-semibold uppercase tracking-widest text-orange-300">For providers</div>
          <h3 className="font-display font-bold text-2xl text-white mt-1">Claim and upgrade your listing.</h3>
          <p className="mt-2 text-blue-100">Get a verified badge, lead routing, priority placement and access to qualified buyer enquiries.</p>
          <Link to="/pricing" className="mt-6 inline-flex items-center gap-2 bg-white text-[#002FA7] font-semibold px-5 py-2.5 rounded-md hover:bg-blue-50" data-testid="cta-provider-list">
            View listing tiers <ArrowRight size={16}/>
          </Link>
        </div>
      </section>
    </div>
  );
}
